import { useState } from 'react'
import { Copy, Check, AlertTriangle, KeyRound } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  usePostAgentsRegenerateToken,
} from '@/api/control/control-plane/control-plane'
import type { AgentDto, RegenerateTokenResponse } from '@/api/control/missionImpactAPI.schemas'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface RegenerateTokenDialogProps {
  orgId: string
  agent: AgentDto
}

/**
 * Dialog for regenerating an agent's registration token.
 * Shows a confirmation prompt, then displays the new one-time token.
 */
export function RegenerateTokenDialog({ orgId, agent }: RegenerateTokenDialogProps) {
  const { t } = useLanguage()
  const { handleApiError } = useError()
  const [isOpen, setIsOpen] = useState(false)
  const [result, setResult] = useState<RegenerateTokenResponse | null>(null)
  const [tokenCopied, setTokenCopied] = useState(false)

  const { mutate: regenerateToken, isPending } = usePostAgentsRegenerateToken({
    mutation: {
      onSuccess: (response) => {
        setResult(response.data)
      },
      onError: (error) => {
        handleApiError(error, { context: 'RegenerateTokenDialog.regenerateToken' })
      },
    },
  })

  const handleRegenerate = () => {
    regenerateToken({ orgId, agentId: agent.id })
  }

  const handleCopyToken = async () => {
    if (!result?.registrationToken) return
    try {
      await navigator.clipboard.writeText(result.registrationToken)
      setTokenCopied(true)
      toast.success(t.agents.regenerateTokenDialog.tokenCopied)
      setTimeout(() => { setTokenCopied(false); }, 2000)
    } catch {
      // Clipboard API may fail in some contexts
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setResult(null)
      setTokenCopied(false)
    }
    setIsOpen(open)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t.agents.regenerateTokenDialog.triggerLabel}>
          <KeyRound className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t.agents.regenerateTokenDialog.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t.agents.regenerateTokenDialog.registrationToken}</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={result.registrationToken ?? ''}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => { void handleCopyToken(); }}
                  >
                    {tokenCopied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {result.tokenExpiresAtUtc && (
                <p className="text-sm text-muted-foreground">
                  {t.agents.regenerateTokenDialog.tokenExpires}: {new Date(result.tokenExpiresAtUtc).toLocaleString()}
                </p>
              )}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t.agents.regenerateTokenDialog.tokenWarning}
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button onClick={() => { handleOpenChange(false); }}>
                {t.agents.regenerateTokenDialog.close}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t.agents.regenerateTokenDialog.title}</DialogTitle>
              <DialogDescription>
                {t.agents.regenerateTokenDialog.description.replace('{name}', agent.name)}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { handleOpenChange(false); }}
                disabled={isPending}
              >
                {t.agents.regenerateTokenDialog.cancel}
              </Button>
              <LoadingButton
                type="button"
                onClick={handleRegenerate}
                isLoading={isPending}
              >
                {t.agents.regenerateTokenDialog.regenerate}
              </LoadingButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
